const inProcessLocks = new Map<string, Promise<void>>();

export interface SyncExclusiveLock {
  run<Result>(key: string, operation: () => Promise<Result>): Promise<Result>;
}

const runInProcess = async <Result>(key: string, operation: () => Promise<Result>): Promise<Result> => {
  const predecessor = inProcessLocks.get(key) ?? Promise.resolve();
  let release: () => void = () => undefined;
  const current = new Promise<void>((resolve) => { release = resolve; });
  const tail = predecessor.then(() => current);
  inProcessLocks.set(key, tail);
  await predecessor;
  try {
    return await operation();
  } finally {
    release();
    if (inProcessLocks.get(key) === tail) inProcessLocks.delete(key);
  }
};

export const syncExclusiveLock: SyncExclusiveLock = {
  run<Result>(key: string, operation: () => Promise<Result>): Promise<Result> {
    const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
    if (!locks) return runInProcess(key, operation);
    return locks.request(`leitour-sync:${key}`, { mode: 'exclusive' }, operation);
  }
};

export const calculateRetryDelay = (
  attempt: number,
  random = Math.random,
  baseMs = 1_000,
  ceilingMs = 5 * 60_000
): number => {
  const exponential = Math.min(ceilingMs, baseMs * 2 ** Math.max(0, attempt - 1));
  return Math.round(exponential * (0.75 + random() * 0.5));
};

export const nextRetryAt = (attempt: number, now = Date.now(), random = Math.random): string =>
  new Date(now + calculateRetryDelay(attempt, random)).toISOString();
